import { useState, type FormEvent } from 'react';
import { api, ApiRequestError } from '../api.ts';
import { useAuth } from '../auth/useAuth.ts';

interface RegisterFormProps {
  onSwitchToLogin: () => void;
}

export function RegisterForm({ onSwitchToLogin }: RegisterFormProps) {
  const { register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Checked by default (T-090) — the briefing is a genuinely useful weekly
  // email shown plainly here rather than buried in a collapsed panel, and
  // one click turns it off, in this form or later in EmailPreferences.
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await register({ email, password, name });
      // Every account defaults to opted out (see EmailPreferences); this
      // only ever turns it on, so a registration that never reaches this
      // line — a failed or abandoned one — leaves that default untouched
      // and subscribes nobody. Best-effort: the account exists either way,
      // so a failed save here doesn't get reported as a registration error.
      if (emailOptIn) {
        try {
          await api.updatePreferences(true);
        } catch {
          // Not fatal — the manager can still opt in later from
          // EmailPreferences.
        }
      }
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Something went wrong registering.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="panel" aria-labelledby="register-heading">
      {/* h2, not h3 (T-100): the first heading under the masthead's h1 on
       * the signed-out login/register route (see App.tsx's `LoginGate`) — an
       * h3 here skipped a level. */}
      <h2 id="register-heading">Create an account</h2>
      <p className="hint">Takes a minute — then your squads are saved against your login.</p>

      <form onSubmit={onSubmit}>
        <label htmlFor="register-name">Name</label>
        <input
          id="register-name"
          type="text"
          autoComplete="name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <label htmlFor="register-email" className="mt-lg">
          Email
        </label>
        <input
          id="register-email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <label htmlFor="register-password" className="mt-lg">
          Password
        </label>
        <input
          id="register-password"
          type="password"
          autoComplete="new-password"
          minLength={8}
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <label className="checkbox-field mt-lg">
          <input
            type="checkbox"
            checked={emailOptIn}
            onChange={(e) => setEmailOptIn(e.target.checked)}
          />
          Deadline briefing
        </label>
        <p className="hint">
          One email before each gameweek deadline, with injury news and price changes for your
          squad. Unsubscribe anytime.
        </p>

        {error && (
          <ul className="notices mt-xl">
            <li className="error">{error}</li>
          </ul>
        )}

        <div className="row">
          <button type="submit" disabled={submitting} aria-busy={submitting}>
            {submitting && <span className="spinner" aria-hidden="true" />}
            {submitting ? 'Creating account…' : 'Create account'}
          </button>
        </div>
      </form>

      <p className="hint mt-xl">Already have an account?</p>
      <div className="row">
        <button type="button" className="ghost" onClick={onSwitchToLogin}>
          Sign in
        </button>
      </div>
    </section>
  );
}
